'use client'

import { useCallback, useRef, useState, useSyncExternalStore } from 'react'
import { baseItems, CATEGORIES } from '../_lib/data'
import { clearUnlockCookie, clearUnlockedState, getUnlockedSnapshot } from '../_lib/unlock'
import type { CategoryKey, Screen, SpendCheckState } from '../types'

const freshState = (): SpendCheckState => ({
  screen: 'intro',
  pool: CATEGORIES.map((c) => c.k),
  imp: [],
  items: baseItems(),
  picked: {},
  top: [],
  moved: [],
})

/** Move `key` one slot up (-1) or down (+1) within an ordered list. */
const nudged = <T>(list: T[], key: T, direction: -1 | 1): T[] => {
  const i = list.indexOf(key)
  const j = i + direction
  if (i < 0 || j < 0 || j >= list.length) return list
  const next = [...list]
  const [moved] = next.splice(i, 1)
  if (moved === undefined) return list
  next.splice(j, 0, moved)
  return next
}

const emptySubscribe = () => () => {}

export function useSpendCheck() {
  // Server snapshot stays locked; client re-reads cookie + sessionStorage after hydration.
  // getUnlockedSnapshot caches by raw storage string so getSnapshot stays Object.is-stable
  // (a fresh parse each render is React error #185 after unlock).
  const unlockedSnapshot = useSyncExternalStore(emptySubscribe, getUnlockedSnapshot, () => null)
  const [override, setOverride] = useState<SpendCheckState | null>(null)
  const state = override ?? unlockedSnapshot ?? freshState()

  // A ref, not state: two adds in the same batch would both read the same rendered count
  // and mint the same id, colliding in `items` and `picked`.
  const customCount = useRef(1)

  const setState = useCallback((updater: (s: SpendCheckState) => SpendCheckState) => {
    setOverride((prev) => {
      const base = prev ?? getUnlockedSnapshot() ?? freshState()
      return updater(base)
    })
  }, [])

  const goTo = (screen: Screen) => {
    setState((s) => ({ ...s, screen }))
    // Each step is a full-height view; landing mid-page reads as a broken transition.
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  /** Unlock → results: carry CTA eligibility into React state (sessionStorage alone isn't enough). */
  const showResults = (showAdvisorCta: boolean) => {
    setState((s) => ({ ...s, screen: 'result', showAdvisorCta }))
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  const actions = {
    rankValue: (k: CategoryKey) =>
      setState((s) => (s.imp.includes(k) ? s : { ...s, pool: s.pool.filter((x) => x !== k), imp: [...s.imp, k] })),

    unrankValue: (k: CategoryKey) =>
      setState((s) => ({ ...s, imp: s.imp.filter((x) => x !== k), pool: [...s.pool, k] })),

    nudgeValue: (k: CategoryKey, direction: -1 | 1) => setState((s) => ({ ...s, imp: nudged(s.imp, k, direction) })),

    /** Selecting an expense off also drops it from the biggest-spend ranking. */
    toggleExpense: (id: string) =>
      setState((s) => {
        if (!s.picked[id]) return { ...s, picked: { ...s.picked, [id]: true as const } }
        const picked = Object.fromEntries(Object.entries(s.picked).filter(([key]) => key !== id)) as Record<
          string,
          true
        >
        return {
          ...s,
          picked,
          top: s.top.filter((x) => x !== id),
          moved: s.moved.filter((m) => m.id !== id),
        }
      }),

    /** Reassign a dual-category expense, remembering the origin so it can be undone. */
    reassignExpense: (id: string, cat: CategoryKey) =>
      setState((s) => {
        let from: CategoryKey | null = null
        const items = s.items.map((it) => {
          if (it.id !== id) return it
          from = it.cat
          return { ...it, cat }
        })
        const prior = s.moved.find((m) => m.id === id)
        const origin = prior ? prior.from : from
        const rest = s.moved.filter((m) => m.id !== id)
        return {
          ...s,
          items,
          moved: origin && origin !== cat ? [...rest, { id, from: origin, to: cat }] : rest,
        }
      }),

    undoReassign: (id: string) =>
      setState((s) => {
        const move = s.moved.find((m) => m.id === id)
        if (!move) return s
        return {
          ...s,
          items: s.items.map((it) => (it.id === id ? { ...it, cat: move.from } : it)),
          moved: s.moved.filter((m) => m.id !== id),
        }
      }),

    dismissReassign: (id: string) => setState((s) => ({ ...s, moved: s.moved.filter((m) => m.id !== id) })),

    addCustomExpense: (cat: CategoryKey, label: string) => {
      const trimmed = label.trim()
      if (!trimmed) return
      const id = `own#${customCount.current++}`
      setState((s) => ({
        ...s,
        items: [...s.items, { id, t: trimmed, cat }],
        picked: { ...s.picked, [id]: true as const },
      }))
    },

    rankExpense: (id: string) => setState((s) => (s.top.includes(id) ? s : { ...s, top: [...s.top, id] })),

    unrankExpense: (id: string) => setState((s) => ({ ...s, top: s.top.filter((x) => x !== id) })),

    nudgeExpense: (id: string, direction: -1 | 1) => setState((s) => ({ ...s, top: nudged(s.top, id, direction) })),

    restart: () => {
      clearUnlockCookie()
      clearUnlockedState()
      setOverride(freshState())
      customCount.current = 1
      window.scrollTo({ top: 0, behavior: 'auto' })
    },
  }

  return { state, goTo, showResults, ...actions }
}
